import React from 'react';
import {
  ThemeOptions,
  ThemeOverride,
} from '@wix/da-react-context/pkg/ThemeContext';
import DefaultPopoverContent from '../../_partials/DefaultPopoverContent';
import PopperPositionedPopover from '../../PopperPositionedPopover';
import type { PopperPositioningProps, PrimaryPlacement } from '../../types';

interface Props extends PopperPositioningProps {
  popoverContent:
    | React.ReactNode
    | ((props: {
        placement: PrimaryPlacement;
        updatePositioning?: () => void;
      }) => React.ReactNode);
  usePortal?: boolean;
  showArrow?: boolean;
  className?: string;
  portalClassName?: string;
  wrapperClassName?: string;
  contentClassName?: string;
  arrowClassName?: string;
  width?: React.CSSProperties['width'];
  height?: React.CSSProperties['height'];
  forceTheme?: ThemeOptions;
  children?: React.ReactNode;
  onClickOutside?: (event: MouseEvent) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLElement>) => void;
}

/** Use when you just need to stick a thing next to another thing
 *  Please note that this component is not DS-conforming */
const SimplePopperPopover: React.FC<Props> = ({
  children,
  popoverContent,
  usePortal,
  className,
  wrapperClassName,
  contentClassName,
  arrowClassName,
  portalClassName,
  showArrow,
  forceTheme,
  width,
  height,
  isVisible,
  placement,
  secondaryPlacement,
  offsetModifier,
  allowFlip,
  positionFixed,
  eventsEnabled,
  referenceElement,
  onClickOutside,
  onMouseLeave,
}) => {
  return (
    <PopperPositionedPopover
      isVisible={isVisible}
      placement={placement}
      secondaryPlacement={secondaryPlacement}
      offsetModifier={offsetModifier}
      allowFlip={allowFlip}
      positionFixed={positionFixed}
      eventsEnabled={eventsEnabled}
      referenceElement={referenceElement}
      renderPopover={({
        effectivePlacement,
        effectiveSecondaryPlacement,
        rootRef,
        arrowRef,
        arrowStyle,
        contentStyle,
        arrowAttributes,
        contentAttributes,
        updatePositioning,
      }) => {
        const content = (
          <DefaultPopoverContent
            effectivePlacement={effectivePlacement}
            effectiveSecondaryPlacement={effectiveSecondaryPlacement}
            rootRef={rootRef}
            arrowRef={arrowRef}
            contentStyle={contentStyle}
            arrowStyle={arrowStyle}
            contentAttributes={contentAttributes}
            arrowAttributes={arrowAttributes}
            updatePositioning={updatePositioning}
            showArrow={showArrow}
            contentClassName={contentClassName}
            arrowClassName={arrowClassName}
            wrapperClassName={wrapperClassName}
            width={width}
            height={height}
            onClickOutside={onClickOutside}
            onMouseLeave={onMouseLeave}
          >
            {typeof popoverContent === 'function'
              ? popoverContent({
                  placement: effectivePlacement,
                  updatePositioning,
                })
              : popoverContent}
          </DefaultPopoverContent>
        );

        return forceTheme ? (
          <ThemeOverride theme={forceTheme!}>{content}</ThemeOverride>
        ) : (
          content
        );
      }}
      usePortal={usePortal}
      className={className}
      portalClassName={portalClassName}
    >
      {children}
    </PopperPositionedPopover>
  );
};
SimplePopperPopover.displayName = 'SimplePopperPopover';

export default SimplePopperPopover;
